// Values several screens work out from the account's data.

import type { Category } from "../domain/categories.ts";
import type { Trip, Vehicle } from "../domain/model.ts";
import type { DistanceUnit } from "../domain/rates.ts";
import type { AccountData } from "./AccountData.tsx";

export function vehicleById(data: AccountData): Map<string, Vehicle> {
  return new Map(data.vehicles.map((vehicle) => [vehicle.id.toUpperCase(), vehicle]));
}

export function vehicleOf(trip: Trip, vehicles: Map<string, Vehicle>): Vehicle | undefined {
  return trip.vehicleId ? vehicles.get(trip.vehicleId.toUpperCase()) : undefined;
}

/**
 * The unit for totals, as the app's TripDefaults picks it: the chosen vehicle's, the only unit the
 * vehicles use, then the newest rate's, then miles.
 */
export function displayUnit(data: AccountData, vehicleId?: string): DistanceUnit {
  if (vehicleId) {
    const vehicle = data.vehicles.find((candidate) => candidate.id.toUpperCase() === vehicleId.toUpperCase());
    if (vehicle) return vehicle.unit;
  }
  const units = new Set(data.vehicles.map((vehicle) => vehicle.unit));
  if (units.size === 1) return [...units][0]!;
  const newestRate = [...data.rateRows].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())[0];
  return newestRate?.unit ?? "miles";
}

/** The unit a trip's own distance shows in: its vehicle's. */
export function tripUnit(trip: Trip, vehicles: Map<string, Vehicle>, fallback: DistanceUnit): DistanceUnit {
  return vehicleOf(trip, vehicles)?.unit ?? fallback;
}

export function categoryOf(trip: Trip, data: AccountData): Category | undefined {
  return data.catalog.resolve(trip.categoryRaw);
}

/** What the trip is worth at its category's rate, or undefined. */
export function tripValue(trip: Trip, data: AccountData): number | undefined {
  const category = categoryOf(trip, data);
  return category ? data.rates.deduction(trip.meters, category.rawValue, trip.start) : undefined;
}

/** The currency the account's rates use. */
export function currencyOf(data: AccountData): string {
  return data.rates.currencyCode ?? data.rateRows[0]?.currencyCode ?? "USD";
}
